// Core data types for Planogram Subtask Management
// 严格按照PRD字段定义和状态机设计

// 子任务状态枚举 - 严格按照PRD状态机定义
export type SubtaskStatus = 'Pending' | 'Active' | 'Ongoing' | 'Finished' | 'Cancelled' | 'Paused';

// 状态操作映射 - 根据PRD状态机定义
export const STATUS_ACTIONS: Record<SubtaskStatus, string[]> = {
  'Pending': ['view'],                    // 未开始：只能查看详情
  'Active': ['view', 'start'],           // 待开始：可查看详情和开始执行
  'Ongoing': ['view', 'complete'],       // 执行中：可查看详情和完成任务
  'Finished': ['view'],                  // 已完成：只能查看详情
  'Cancelled': ['view'],                 // 已取消：只能查看详情
  'Paused': ['view']                     // 暂停：只能查看详情，需要通过计划启动来恢复
};

// 状态显示文本映射
export const STATUS_TEXT: Record<SubtaskStatus, string> = {
  'Pending': '未开始',
  'Active': '待开始', 
  'Ongoing': '执行中',
  'Finished': '已完成',
  'Cancelled': '已取消',
  'Paused': '暂停'
};

// 主要子任务实体 - 严格按照PRD字段定义
export interface PlanogramSubtask {
  id: number,
  subtaskCode: string;                  // 子任务唯一标识代码，系统自动生成，格式为ST+6位数字
  subtaskName: string;                  // 子任务的具体名称，描述任务的核心内容和目标
  milestoneName: string;              // 子任务所属的里程碑名称，标识任务在整个计划中的层级关系
  assignee: string;                     // 具体执行该子任务的人员姓名，负责任务的实施和状态更新
  status: SubtaskStatus;                // 子任务当前执行状态，包括未开始、待开始、执行中、已完成等状态
  gmtCreate: string;                    // 子任务在系统中创建的日期时间，由陈列计划生成时自动记录
  creatorName: string;                  // 创建该子任务的系统用户或流程标识，记录任务来源
  milestoneStartDate: string;           // 所属里程碑的计划开始日期，用于计算任务执行时间窗口
  startDate?: string;                   // 子任务实际开始执行的日期时间，由执行人员确认后记录
  dueDate: string;                      // 子任务的计划截止日期，基于里程碑时间自动计算，用于进度控制
  completionDate?: string;              // 子任务实际完成的时间，由执行人员确认完成后系统记录
  deliverables?: string;                // 子任务执行过程中产生的交付物清单，包括文档、截图、配置文件等
  remarks?: string;                     // 执行人员填写的任务执行说明，记录具体实施过程和关键节点
  statusChangeHistory: StatusChange[];  // 子任务状态变更的完整历史记录，包括变更时间、变更人、变更原因等
  autoStatusUpdate: boolean;            // 标识该子任务是否启用自动状态更新机制，如超时自动标记等
  overdueFlag: boolean;                 // 标识子任务是否已超过计划截止时间，用于超时任务的特殊标识和处理
  planEndDate: string;                  // 子任务所属里程碑的计划结束日期，用于计算整体进度和超时判断
  finalStatusDate?: string;             // 子任务最终状态确认的日期时间，通常为完成或结束状态的时间戳
  planCode: string;              // 关联陈列计划编码，标识子任务所属的陈列计划
  planName: string;              // 关联陈列计划名称，便于用户识别和管理
}

// Status change record
export interface StatusChange {
  id: string;
  fromStatus: SubtaskStatus | null;
  toStatus: SubtaskStatus;
  changeTime: string;
  changedBy: string;
  reason: string;
  notes?: string;
}

// Comment system
export interface SubtaskComment {
  id: string;
  subtaskCode: string;
  content: string;
  author: string;
  gmtCreate: string;
  updateTime?: string;
}

// Attachment system
export interface SubtaskAttachment {
  id: string;
  subtaskCode: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  uploadTime: string;
  uploader: string;
  downloadUrl: string;
}

// Filter criteria
export interface SubtaskFilters {
  planCode?: string;
  status?: SubtaskStatus | 'all';
  milestone?: string;
  assignee?: string;
  dueDateStart?: number;
  dueDateEnd?: number;
  overdueFlag?: boolean | 'all';
  search?: string;
}

// Pagination parameters
export interface PaginationParams {
  current: number;
  pageSize: number;
}

// API response wrapper - following frontend development standards
export interface ApiResponse<T> {
  traceId: string;
  success: boolean;
  errorMessage: string | null;
  flag: 'success' | 'fail';
  data: T;
  msg?: string | null;
  errorCode?: string | null;
  code?: string | null;
  message?: string | null;
}

// Component interfaces

// Main list component props
export interface SubtaskListProps {
}

// Detail view component props
export interface SubtaskDetailProps {
  subtaskCode: string;
  onBack: () => void;
  onStatusChange: (newStatus: SubtaskStatus) => void;
}

// Filter panel component props
export interface FilterPanelProps {
  filters: SubtaskFilters;
  onFiltersChange: (filters: SubtaskFilters) => void;
  onSearch: () => void;
  onReset: () => void;
  loading?: boolean;
  milestones: string[];
  assignees: string[];
}

// Task actions component props
export interface TaskActionsProps {
  subtask: PlanogramSubtask;
  onStart: () => Promise<void>;
  onComplete: (executionNotes: string, files?: File[]) => Promise<void>;
  loading?: boolean;
  showCard?: boolean;
}

// Status tag component props
export interface StatusTagProps {
  status: SubtaskStatus;
  overdueFlag: boolean;
  size?: 'small' | 'default';
  showIcon?: boolean;
}

// Subtask table component props
export interface SubtaskTableProps {
  subtasks: PlanogramSubtask[];
  loading: boolean;
  pagination: {
    current: number;
    pageSize: number;
    total: number;
  };
  onRowClick: (subtask: PlanogramSubtask) => void;
  onPageChange: (page: number, pageSize: number) => void;
  onStatusUpdate: (subtaskCode: string, newStatus: SubtaskStatus) => void;
  onCompleteTask: (subtask: PlanogramSubtask) => void;
}

// Comment section component props
export interface CommentSectionProps {
  subtaskCode: string;
  comments: SubtaskComment[];
  onAddComment: (content: string) => Promise<void>;
  loading: boolean;
  showCard?: boolean;
}

// Attachment manager component props
export interface AttachmentManagerProps {
  subtaskCode: string;
  attachments: SubtaskAttachment[];
  onUpload: (file: File) => Promise<void>;
  onDelete: (attachmentId: string) => Promise<void>;
  loading: boolean;
  showCard?: boolean;
}

// Status history component props
export interface StatusHistoryProps {
  statusHistory: StatusChange[];
}

// Error state interface
export interface ErrorState {
  hasError: boolean;
  message: string;
  details?: string;
  errorCode?: string;
}

// Loading state interface
export interface LoadingState {
  list: boolean;
  detail: boolean;
  statusUpdate: boolean;
  comments: boolean;
  attachments: boolean;
}

// Validation error interface
export interface ValidationError {
  field: string;
  message: string;
  type: 'error' | 'warning';
  code?: string;
}

// Validation result interface
export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
  warnings?: ValidationError[];
}

// Validation rule interface
export interface ValidationRule {
  field: string;
  required?: boolean;
  type?: 'string' | 'number' | 'boolean' | 'array' | 'object' | 'date';
  minLength?: number;
  maxLength?: number;
  min?: number;
  max?: number;
  pattern?: RegExp;
  message?: string;
  custom?: (value: any, allData?: any) => string | null;
}

// Validation schema interface
export interface ValidationSchema {
  rules: ValidationRule[];
  dependencies?: Record<string, string[]>;
}

// File upload constraints
export interface FileUploadConstraints {
  maxFiles: number;
  maxFileSize: number; // in bytes
  allowedTypes: string[];
}

// Default file upload constraints
export const DEFAULT_FILE_UPLOAD_CONSTRAINTS: FileUploadConstraints = {
  maxFiles: 10,
  maxFileSize: 50 * 1024 * 1024, // 50MB
  allowedTypes: ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.png', '.jpg', '.jpeg', '.txt']
};

// Status configuration with icons and Ant Design colors - following plan management design
export const SUBTASK_STATUS_CONFIG: Record<SubtaskStatus, {
  color: string;
  text: string;
  icon: string;
}> = {
  'Pending': {
    color: 'default',        // Ant Design default gray
    text: '待开始',
    icon: 'ClockCircleOutlined'
  },
  'Active': {
    color: 'processing',     // Ant Design blue
    text: '可开始',
    icon: 'PlayCircleOutlined'
  },
  'Ongoing': {
    color: 'warning',        // Ant Design orange
    text: '进行中',
    icon: 'SyncOutlined'
  },
  'Finished': {
    color: 'success',        // Ant Design green
    text: '已完成',
    icon: 'CheckCircleOutlined'
  },
  'Cancelled': {
    color: 'error',          // Ant Design red
    text: '已取消',
    icon: 'StopOutlined'
  },
  'Paused': {
    color: 'warning',        // Ant Design orange for paused state
    text: '暂停',
    icon: 'PauseCircleOutlined'
  }
};

// Legacy support - Status color mapping for UI (deprecated, use SUBTASK_STATUS_CONFIG instead)
export const STATUS_COLORS: Record<SubtaskStatus, string> = {
  'Pending': '#d9d9d9',     // Gray
  'Active': '#1890ff',      // Blue
  'Ongoing': '#faad14',     // Orange
  'Finished': '#52c41a',    // Green
  'Cancelled': '#ff4d4f',   // Red
  'Paused': '#faad14'       // Orange for paused state
};

// Legacy support - Status display names (deprecated, use SUBTASK_STATUS_CONFIG instead)
export const STATUS_DISPLAY_NAMES: Record<SubtaskStatus, string> = {
  'Pending': '待开始',
  'Active': '可开始',
  'Ongoing': '进行中',
  'Finished': '已完成',
  'Cancelled': '已取消',
  'Paused': '暂停'
};

// Action button configurations based on status - removed duplicate, using definition above

// Date format constants
export const DATE_FORMATS = {
  DISPLAY_DATE: 'YYYY-MM-DD',
  DISPLAY_DATETIME: 'YYYY-MM-DD HH:mm:ss',
  API_DATE: 'YYYY-MM-DD',
  API_DATETIME: 'YYYY-MM-DDTHH:mm:ss.SSSZ'
};

// Empty state messages
export const EMPTY_STATE_MESSAGES = {
  NO_SUBTASKS: 'No subtask data available',
  NO_SEARCH_RESULTS: 'No matching subtasks found',
  NO_COMMENTS: 'No comments',
  NO_ATTACHMENTS: 'No attachments',
  NO_STATUS_HISTORY: 'No status change records'
};

// Error messages
export const ERROR_MESSAGES = {
  NETWORK_ERROR: '网络连接失败，请检查网络后重试',
  SERVER_ERROR: '服务器内部错误，请稍后重试',
  PERMISSION_ERROR: '您没有权限执行此操作',
  VALIDATION_ERROR: '输入数据验证失败',
  NOT_FOUND_ERROR: '请求的资源不存在',
  BUSINESS_LOGIC_ERROR: '业务逻辑错误'
};

// Success messages
export const SUCCESS_MESSAGES = {
  SUBTASK_STARTED: '任务已开始',
  SUBTASK_COMPLETED: '任务已完成',
  COMMENT_ADDED: '评论已添加',
  ATTACHMENT_UPLOADED: '附件上传成功',
  ATTACHMENT_DELETED: '附件删除成功'
};

// Utility functions for getting status configurations - following plan management design
export const getSubtaskStatusConfig = (status: SubtaskStatus) => {
  return SUBTASK_STATUS_CONFIG[status];
};

export const getAllSubtaskStatusOptions = () => {
  return Object.entries(SUBTASK_STATUS_CONFIG).map(([key, value]) => ({
    value: key,
    label: value.text,
    color: value.color,
    icon: value.icon
  }));
};